import React, { FC, memo, useState } from 'react';
import { IActionItem } from '@components/ActionsMenu/types';
import { ContextMenu } from '@components/MainStage/ContextMenu/ContextMenu';
import { DeleteNodeModal } from '@components/MainStage/ContextMenu/NodeContextMenu/DeleteNodeModal';
import RelationshipsMenu from '@components/MainStage/ContextMenu/RelationshipsMenu';
import { isFocusEligible } from '@components/MainStage/focusOn.helpers';
import {
  useIsFocusActive,
  useIsNodeFocused,
} from '@components/MainStage/focusOn.store';
import useDeleteKey from '@components/MainStage/hooks/useDeleteKey';
import useFocusOn from '@components/MainStage/hooks/useFocusOn';
import useIsNodeAddedToAsset from '@components/MainStage/hooks/useIsNodeAddedToAsset';
import {
  getMultiSelectionState,
  useMultiSelectionCount,
} from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { ContextMenuProps } from '@components/MainStage/types';
import { NODE_MENU_ITEM_HEIGHT } from '@constants/canvas/general';
import { useProject } from '@context/Project/ProjectProvider';
import useAssets from '@hooks/useAssets';
import useCanEditModel from '@hooks/useCanEditModel';
import useToggle from '@hooks/useToggle';
import {
  NodeActionType,
  NodeDTO,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import { themePalette } from '@theme/muiTheme';
import {
  CloseIcon,
  FocusOnIcon,
  RelationshipsIcon,
  ReportIcon,
  TrashIcon,
} from '@utils/iconsDefs';
import {
  getMostCapableNode,
  isTargetScopeVisible,
  NODE_ACTIONS,
} from '@utils/nodeActions';
import { JustificationReportModal } from '@views/ReportsModel/components/JustificationReport/JustificationReportModal';

import { AddScopeModal } from './AddScopeModal';
import { NodeActionSubmenu } from './NodeActionSubmenu';

const NodeContextMenu: FC<ContextMenuProps> = ({ isOpen, onClose, menu }) => {
  const { getNodeById } = useStore();
  const [isDeleteOpen, deleteControls] = useToggle<null | string>();
  const [isJustificationReportOpen, justificationReportControls] = useToggle<
    null | boolean
  >();
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const [isScopeOpen, scopeControls] = useToggle<boolean>();
  const [scopeTargetIds, setScopeTargetIds] = useState<string[]>([]);
  const canEdit = useCanEditModel();

  const nodeId = deleteId || menu?.id || '';
  const isNodeAdded = useIsNodeAddedToAsset(nodeId);
  const node = getNodeById(nodeId);

  const multiSelectionCount = useMultiSelectionCount();
  const { enterFocus, exitFocus } = useFocusOn();
  const isFocusActive = useIsFocusActive();
  const isNodeInFocus = useIsNodeFocused(nodeId);

  const canShowFocusOn =
    !isFocusActive && !!node && isFocusEligible(node, multiSelectionCount);
  const canShowExitFocus = isFocusActive && isNodeInFocus;

  const isNodeWithJustificationReport =
    node?.data?.dto?.type === NodeDTOTypes.BusinessAttribute ||
    node?.data?.dto?.type === NodeDTOTypes.SecurityAttribute;

  const {
    toolbox: { mode },
  } = useProject();

  const { selectedAssetId } = useAssets();
  const { selectedIds } = getMultiSelectionState();
  const targetIds =
    selectedIds.length > 1 && selectedIds.includes(nodeId)
      ? selectedIds
      : [nodeId];
  const targetNodes = targetIds
    .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
    .filter((dto): dto is NodeDTO => Boolean(dto));
  const representativeNode = mode
    ? getMostCapableNode(targetNodes, mode)
    : undefined;
  const showTargetScope =
    canEdit && mode
      ? isTargetScopeVisible(targetNodes, mode, selectedAssetId)
      : false;
  const targetScopeConfig = NODE_ACTIONS[NodeActionType.AddTargetScope];
  const actionMenuItems: IActionItem[] =
    canEdit && mode && representativeNode
      ? Object.values(NODE_ACTIONS)
          .filter(
            (config) =>
              config.statusOptions &&
              config.getEligibility(representativeNode, mode).eligible,
          )
          .map((config) => ({
            icon: config.icon,
            text: config.label,
            subComponent: (
              <NodeActionSubmenu
                config={config}
                nodeIds={targetIds}
                onCloseMenu={onClose}
              />
            ),
          }))
      : [];

  const handleOpenDeleteModal = () => {
    deleteControls.on();
    onClose();
    setDeleteId(null);
  };

  const handleOpenJustificationReportModal = () => {
    justificationReportControls.on();
    onClose();
  };

  const handleOpenScopeModal = () => {
    setScopeTargetIds(targetIds);
    scopeControls.on();
  };

  const relationsMenuItem: IActionItem = {
    icon: <RelationshipsIcon fill="currentColor" />,
    text: 'Relationships',
    subComponent: <RelationshipsMenu nodeId={nodeId} onCloseMenu={onClose} />,
    popoverProps: {
      sx: {
        '& .MuiPopover-paper': { borderRadius: 2 },
        '.MuiPaper-root': {
          width: 320,
          maxHeight: 500,
          overflow: 'hidden',
          display: 'flex',
        },
      },
    },
  };

  const contextMenuItems: IActionItem[] = [
    ...(canShowFocusOn
      ? [
          {
            icon: <FocusOnIcon fill="currentColor" />,
            text: 'Focus on',
            onClick: () => {
              enterFocus(nodeId, node!.data.dto.name);
              onClose();
            },
          },
        ]
      : []),

    ...(canShowExitFocus
      ? [
          {
            icon: <FocusOnIcon fill="currentColor" />,
            text: 'Exit focus on',
            onClick: () => {
              exitFocus();
              onClose();
            },
          },
        ]
      : []),

    relationsMenuItem,

    ...actionMenuItems,

    ...(showTargetScope && targetScopeConfig
      ? [
          {
            icon: targetScopeConfig.icon,
            text: targetScopeConfig.label,
            onClick: handleOpenScopeModal,
          },
        ]
      : []),

    ...(isNodeWithJustificationReport
      ? [
          {
            icon: <ReportIcon fill="currentColor" />,
            text: 'Reports',
            subItems: [
              {
                text: 'Justification statement',
                onClick: handleOpenJustificationReportModal,
              },
            ],
          },
        ]
      : []),

    ...(canEdit
      ? [
          {
            icon: <TrashIcon />,
            text: 'Delete',
            divider: isNodeWithJustificationReport,
            disableHoverColor: true,
            color: themePalette.system.error,
            onClick: handleOpenDeleteModal,
          },
        ]
      : []),
  ];

  const removeMenuItems = [
    relationsMenuItem,
    ...(canEdit
      ? [
          {
            icon: <CloseIcon fill={themePalette.grey[1000]} />,
            text: 'Remove entity',
            onClick: handleOpenDeleteModal,
            textColor: 'grey.1000',
          },
        ]
      : []),
  ];

  useDeleteKey('node', (id) => {
    if (!canEdit) return;

    setDeleteId(id);
    deleteControls.on();
  });

  if (contextMenuItems.length === 0) return null;

  const isMultiSelectActive = targetIds.length > 1;
  const menuItems =
    isNodeAdded && !isMultiSelectActive ? removeMenuItems : contextMenuItems;

  return (
    <>
      <DeleteNodeModal
        open={isDeleteOpen}
        onClose={deleteControls.off}
        nodeId={nodeId}
      />

      {mode && (
        <AddScopeModal
          open={isScopeOpen}
          onClose={scopeControls.off}
          nodeIds={scopeTargetIds}
          mode={mode}
        />
      )}

      {isNodeWithJustificationReport && (
        <JustificationReportModal
          open={isJustificationReportOpen}
          onClose={justificationReportControls.off}
          node={node}
        />
      )}

      {isOpen && !isScopeOpen && menu && menu.type === 'node' && (
        <ContextMenu
          menuItems={menuItems}
          {...menu}
          top={menu.top - menuItems.length * NODE_MENU_ITEM_HEIGHT}
        />
      )}
    </>
  );
};

export default memo(NodeContextMenu);
